import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, User, MessageSquare, CheckCircle } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormData({ name: '', email: '', message: '' });

      // Reset success message after 5 seconds
      setTimeout(() => setIsSubmitted(false), 5000);
    }, 2000);
  };

  return (
    <section id="contact" className="book-section bg-book-cream">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-book-brown mb-6">
            Connect With Our Experts
          </h2>
          <p className="text-xl text-book-brown-dark max-w-3xl mx-auto">
            Have questions about our featured platforms, need personalized recommendations, or want to share your gaming experiences?
            Our team of industry specialists is here to assist you every step of your journey.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8" itemScope itemType="https://schema.org/ContactPoint">
            <div>
              <h3 className="text-2xl font-bold text-book-brown mb-6">Get in Touch</h3>
              <p className="text-book-brown-dark mb-8 leading-relaxed">
                Whether you're seeking advice on our recommended platforms, have questions about responsible gaming,
                or want to explore partnership opportunities, we're committed to providing exceptional support
                and genuine expertise.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-book-brown rounded-lg flex items-center justify-center">
                  <Mail className="h-6 w-6 text-book-cream" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-book-brown mb-1">Email Us</h4>
                  <p className="text-book-brown-dark" itemProp="email">info@britember.com</p>
                  <p className="text-sm text-book-brown-light">We respond within 24 hours</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-book-brown rounded-lg flex items-center justify-center">
                  <Phone className="h-6 w-6 text-book-cream" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-book-brown mb-1">Call Us</h4>
                  <p className="text-book-brown-dark" itemProp="telephone">+44 0754 150 516</p>
                  <p className="text-sm text-book-brown-light">Mon-Fri, 9 AM - 6 PM GMT</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-book-brown rounded-lg flex items-center justify-center">
                  <MapPin className="h-6 w-6 text-book-cream" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-book-brown mb-1">Visit Us</h4>
                  <p className="text-book-brown-dark" itemProp="address">London, United Kingdom</p>
                  <p className="text-sm text-book-brown-light">Registered office address</p>
                </div>
              </div>
            </div>

            <div className="bg-book-beige rounded-xl p-6 shadow-book-inset">
              <h4 className="text-lg font-bold text-book-brown mb-3">Why Contact Brit Ember?</h4>
              <ul className="space-y-2 text-book-brown-dark">
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span>Expert gaming industry knowledge</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span>Personalized platform recommendations</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span>Responsible gaming guidance</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span>Confidential and professional support</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-xl shadow-book p-8">
            <h3 className="text-2xl font-bold text-book-brown mb-6">Send Us a Message</h3>

            {isSubmitted && (
              <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <p className="text-green-800 font-medium">Thank you! Your message has been sent successfully. We'll get back to you soon.</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-book-brown mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-5 w-5 text-book-brown-light" />
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    autoComplete="name"
                    className="book-input pl-10 w-full"
                    placeholder="Your full name"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-book-brown mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-5 w-5 text-book-brown-light" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                    className="book-input pl-10 w-full"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-book-brown mb-2">
                  Message
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 h-5 w-5 text-book-brown-light" />
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    autoComplete="off"
                    rows={6}
                    className="book-input pl-10 w-full resize-none"
                    placeholder="Tell us how we can help you..."
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full book-button flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-book-cream border-t-transparent"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    <span>Send Message</span>
                  </>
                )}
              </button>
            </form>

            <p className="text-xs text-book-brown-light mt-4 text-center">
              We respect your privacy and will never share your information with third parties.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
